import * as React from "react"
import { cn } from "~/lib/utils"

import { Check, X, ChevronsUpDown } from "lucide-react"
import { Button } from "~/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandSeparator,
} from "~/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover"
import { Badge } from "~/components/ui/badge"

export type OptionType = {
  label: string
  value: string
}

export interface MultiSelectProps {
  options: OptionType[]
  getLabel?: (id: string) => string
  selected: string[]
  onValuesChange: (ids: Array<string>) => void
  className?: string
  inputValue?: string
  onInputChange?: (value: string) => void
  shouldFilter?: boolean
  id?: string
  name?: string
  onFetchMore?: () => void
  placeholder?: string
  disabled?: boolean
  dividers?: Array<number>
}

function MultiSelect({
  options,
  getLabel,
  selected,
  onValuesChange,
  className,
  shouldFilter,
  placeholder,
  disabled,
  dividers,
  ...props
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)

  const handleUnselect = (item: string) => {
    onValuesChange(selected.filter((i) => i !== item))
  }

  return (
    <Popover open={open} onOpenChange={setOpen} {...props}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between h-full text-foreground`}
          onClick={() => setOpen(!open)}
          disabled={disabled}
        >
          {selected.length === 0 && (
            <div className="text-black/50">{placeholder}</div>
          )}
          <div className="flex gap-2 flex-wrap">
            {selected.length === 0 && (
              <Badge className="mr-1 mb-1 opacity-0">&nbsp;</Badge>
            )}
            {selected.map((id) => (
              <Badge
                variant="accent"
                key={id}
                onClick={() => handleUnselect(id)}
              >
                {getLabel
                  ? getLabel(id)
                  : options.find((o) => o.value === id)?.label}
                <div
                  className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleUnselect(id)
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleUnselect(id)
                  }}
                >
                  <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                </div>
              </Badge>
            ))}
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command className={className} shouldFilter={shouldFilter}>
          <CommandInput
            placeholder="Search ..."
            value={props.inputValue}
            onValueChange={props.onInputChange}
          />
          <CommandEmpty>No item found.</CommandEmpty>
          <CommandGroup className="max-h-64 overflow-auto">
            {options.map((option, index) => (
              <>
                {dividers && dividers.includes(index) ? (
                  <CommandSeparator className="my-1" />
                ) : null}
                <CommandItem
                  key={option.value}
                  onSelect={() => {
                    onValuesChange(
                      selected.includes(option.value)
                        ? selected.filter((item) => item !== option.value)
                        : [...selected, option.value]
                    )
                    setOpen(true)
                  }}
                  value={option.label}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected.includes(option.value)
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              </>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export { MultiSelect }
